import { Tooltip, UI } from '@genial/components';
import { Color, Spacing, Typography } from '@genial/design-tokens';
import * as NB from 'native-base';
import React from 'react';

import { TableContent, TableContentItemColumn } from '../../../../../components/TableContent';
import { getValidatedStatusIcon } from '../../../../../utils/getValidatedStatusIcon';
import { BasketLink } from '../../../../OrderHistoryModal/components/BasketLink';
import { messageError, ordersHistoryTableHead } from '../../constants';

import {
  OrderHistoryItemComponentProps,
  formatDate,
  formatMoney,
  handleExportNewOrder,
} from './constants';

const TextItemTable = ({ value, uppercase }: { value: string; uppercase?: boolean }) => (
  <NB.Text
    numberOfLines={1}
    variant="regular"
    fontSize={Typography['fontsize.xs']}
    textTransform={uppercase ? 'uppercase' : 'inherit'}>
    {value}
  </NB.Text>
);

const OrderHistoryItem = ({
  item,
  onSelect,
  isReferencedByBasket,
  onBasketPress,
}: OrderHistoryItemComponentProps) => {
  const orderRequestedAmount = formatMoney(item?.requestedAmount);

  const orderCreatedAt = formatDate(item?.orderDate);

  const statusItem = getValidatedStatusIcon(item.orderStatus.statusIcon);

  const getFlex = (index: number) => {
    const currentHead = ordersHistoryTableHead.find((_, i) => i === index);
    return currentHead?.flex || 1;
  };

  const renderItemStatus = () => {
    const IconStatus = (
      <NB.HStack space={Spacing['inline.quarck']} alignItems="center">
        <UI.IconStatus
          testId="icon-status"
          bgColor={statusItem.backgroundColor}
          icon={statusItem.icon}
          color={statusItem.iconColor}
          size={statusItem.iconSize}
        />
        {!!item.orderStatus.status && (
          <UI.Text textTransform="uppercase">{item.orderStatus.status}</UI.Text>
        )}
      </NB.HStack>
    );

    const errorMessage = item?.error?.internalMessage || item?.error?.externalMessage;

    if (errorMessage) {
      return (
        <UI.Tooltip label={`Motivo: ${errorMessage}`} testId="status-error-message">
          {IconStatus}
        </UI.Tooltip>
      );
    }
    return IconStatus;
  };

  return (
    <TableContent direction="row" space={0} p={0} px={Spacing['inline.xxxs']} h="70px">
      <TableContentItemColumn flex={getFlex(0)}>
        <NB.Pressable testID="order-history-button-icon" onPress={() => handleExportNewOrder(item)}>
          <UI.Icon
            testId="order-history-icon"
            name="Pdf"
            size="xs"
            color={Color['brand.tertiary.principal']}
          />
        </NB.Pressable>
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(1)}>
        <TextItemTable value={item?.orderHash || messageError} />
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(2)}>
        <NB.HStack space={Spacing['inline.quarck']} alignItems="center">
          <TextItemTable
            value={
              isReferencedByBasket
                ? item?.basketDetail?.basketAdvisorOrderHash || messageError
                : messageError
            }
          />
          {isReferencedByBasket && <BasketLink onPress={onBasketPress} />}
        </NB.HStack>
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(3)}>
        <Tooltip label={item?.sideDescription || messageError}>
          <NB.Box w="full">
            <TextItemTable uppercase value={item?.sideDescription || messageError} />
          </NB.Box>
        </Tooltip>
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(4)}>
        <TextItemTable value={orderCreatedAt} />
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(5)}>
        <Tooltip label={item?.customer?.customerName || messageError}>
          <NB.Box w="full">
            <TextItemTable value={item?.customer?.customerName || messageError} />
          </NB.Box>
        </Tooltip>
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(6)}>
        <TextItemTable value={item?.marketType || messageError} uppercase />
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(7)}>
        <TextItemTable value={orderRequestedAmount} />
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(8)}>{renderItemStatus()}</TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(9)}>
        <NB.Button
          color={Color['brand.tertiary.principal']}
          variant="textLink"
          onPress={() => onSelect(item)}
          rightIcon={
            <UI.Icon
              name="ChevronRight"
              size="xxxs"
              testId="icon"
              color={Color['brand.tertiary.principal']}
            />
          }>
          DETALHES
        </NB.Button>
      </TableContentItemColumn>
    </TableContent>
  );
};

export default React.memo(OrderHistoryItem);
